import { z } from 'zod';
import { ASSETS, CREDITS, SIZES } from '../../ontology/portfolio.ts';
import { SECTORS } from '../../ontology/sectors.ts';
import { day, instant } from './primitives.ts';

/** What a member holds a line of, as Kite and Coin say it. */
const HOLDING_KINDS = ['stock', 'etf', 'fund', 'gold', 'bond', 'trust'] as const;

const assetSchema = z.enum(ASSETS);
const sectorSchema = z.enum(SECTORS);

/** A fund's portfolio as its house disclosed it, or why there is none to look inside. */
const insideSchema = z.discriminatedUnion('read', [
  z.object({ read: z.literal(true), month: day }),
  z.object({
    read: z.literal(false),
    /** Its category alone says what it holds. */
    guessed: assetSchema.nullable(),
    /** False where the desk has no reader for its house's portfolios; absent where its house is unknown. */
    reader: z.boolean().optional(),
  }),
]);

const partsSchema = z.object({
  base: z.number(),
  brokerage: z.number(),
  transaction: z.number(),
  levies: z.number(),
});

const rankedSchema = z.object({
  /** Null among too few. */
  median: z.number().nullable(),
  /** 1 the best; null among too few. */
  rank: z.number().nullable(),
  of: z.number(),
});

const qualitySchema = z.object({
  among: z.object({
    kind: z.enum(['category', 'index']),
    name: z.string(),
    funds: z.number(),
  }),
  asOf: day.nullable(),
  returns: z.array(rankedSchema.extend({ years: z.number(), rate: z.number() })),
  /** Oldest first; empty where it or too few peers go back so far. */
  windows: z.array(
    z.object({
      end: day,
      rate: z.number(),
      median: z.number(),
      low: z.number(),
      high: z.number(),
      quarter: z.number().int().min(1).max(4),
    }),
  ),
  fall: z.object({ fall: z.number(), median: z.number().nullable() }).nullable(),
  cost: rankedSchema.extend({ ratio: z.number(), median: z.number() }).nullable(),
});

export type FundQuality = z.infer<typeof qualitySchema>;

const holdingSchema = z.object({
  /** Its ISIN, else its symbol. */
  key: z.string(),
  name: z.string(),
  symbol: z.string().nullable(),
  kind: z.enum(HOLDING_KINDS),
  /** AMFI's for a fund or an ETF. */
  category: z.string().nullable(),
  house: z.string().nullable(),
  units: z.number(),
  price: z.number(),
  value: z.number(),
  invested: z.number(),
  /**
   * Since the last close; for a fund, whose NAV moves once a day, its latest NAV's move, where the
   * desk has its two latest; else null.
   */
  today: z.number().nullable(),
  /** For a fund whose `today` is said, the day of the NAV it runs to. */
  todayOn: day.optional(),
  plan: z.enum(['direct', 'regular']).nullable(),
  /** A year's expense ratio as a fraction, and what it costs this holding a year in rupees. */
  cost: z
    .object({
      ratio: z.number(),
      yearly: z.number(),
      /** What its direct plan would save a year, for a regular plan whose direct plan is known. */
      directSaves: z.number().nullable(),
      parts: partsSchema.nullable(),
    })
    .nullable(),
  inside: insideSchema.nullable(),
  quality: qualitySchema.nullable(),
  /** A debt fund's; `stated` where its sheet says the yield rather than its lines. */
  debt: z
    .object({
      yield: z.number(),
      afterCost: z.number().nullable(),
      maturityDays: z.number().nullable(),
      month: day,
      stated: z.boolean(),
    })
    .nullable(),
});

export type PortfolioHolding = z.infer<typeof holdingSchema>;

const viaSchema = z.object({
  key: z.string(),
  value: z.number(),
  /** Of the fund. */
  weight: z.number(),
});

const exposureSchema = z.object({
  isin: z.string(),
  name: z.string(),
  symbol: z.string().nullable(),
  sector: sectorSchema,
  industry: z.string().nullable(),
  size: z.enum(SIZES).nullable(),
  foreign: z.boolean(),
  value: z.number(),
  direct: z.number(),
  via: z.array(viaSchema),
});

export type Exposure = z.infer<typeof exposureSchema>;

const shareSchema = z.object({ value: z.number(), benchmark: z.number().nullable() });

const sectorShareSchema = shareSchema.extend({
  sector: sectorSchema,
  industries: z.array(z.object({ industry: z.string(), value: z.number() })),
});

const sizeShareSchema = shareSchema.extend({ size: z.enum([...SIZES, 'unranked']) });

const overlapSchema = z.object({ a: z.string(), b: z.string(), shared: z.number() });

const changeSchema = z.object({
  isin: z.string(),
  name: z.string(),
  symbol: z.string().nullable(),
  /**
   * The member's rupees in it through these funds, their units held as now: at last month's
   * disclosure and at this month's, each at its own month's prices. Not what was traded.
   */
  before: z.number(),
  after: z.number(),
  /** What each fund's manager did to the shares of it the fund holds, by the counts disclosed. */
  by: z.array(
    z.object({
      key: z.string(),
      did: z.enum(['new', 'sold', 'more', 'fewer']),
      /** The change in the shares the fund holds, `0.12` for 12% more; null where new or sold out. */
      shares: z.number().nullable(),
    }),
  ),
});

const FINDINGS = [
  'concentrated',
  'hidden',
  'overlap',
  'regular',
  'sector',
  'small',
  'lagging',
  'costly',
] as const;

const findingSchema = z.object({
  kind: z.enum(FINDINGS),
  title: z.string(),
  detail: z.string(),
  /** What the finding is about, to point at in the page: a stock's ISIN, a fund's key, a sector. */
  about: z.array(z.string()),
});

export type Finding = z.infer<typeof findingSchema>;

const returnsSchema = z
  .object({ from: day, to: day, annual: z.boolean(), rate: z.number() })
  .nullable();

const gainsSchema = z.object({
  short: z.number(),
  long: z.number(),
  unknown: z.number(),
});

const byKindSchema = z.object({ equity: gainsSchema, debt: gainsSchema, other: gainsSchema });

const yoursSchema = z.object({
  from: day,
  funds: z.array(
    z.object({
      key: z.string(),
      since: z.object({ from: day, gain: z.number(), returns: returnsSchema }).nullable(),
    }),
  ),
  index: z
    .object({
      name: z.string(),
      from: day,
      put: z.number(),
      yours: z.number(),
      theirs: z.number(),
    })
    .nullable(),
  tax: z.object({
    year: z.object({ from: day, to: day }),
    knownFrom: day,
    realised: byKindSchema,
    exempt: z.object({ limit: z.number(), left: z.number() }),
    unrealised: byKindSchema,
    holdings: z.array(
      z.object({
        key: z.string(),
        rule: z.object({
          kind: z.enum(['equity', 'debt', 'other']),
          longAfter: z.union([z.literal(12), z.literal(24)]),
        }),
        lots: z.array(
          z.object({
            by: day,
            after: day.nullable(),
            units: z.number(),
            cost: z.number(),
            value: z.number(),
            long: z.boolean().nullable(),
            longFrom: day.nullable(),
            slab: z.boolean().nullable(),
          }),
        ),
      }),
    ),
    /** `sure` where the day is exact, not at the latest. */
    coming: z.array(z.object({ day, key: z.string(), gain: z.number(), sure: z.boolean() })),
  }),
});

export type Yours = z.infer<typeof yoursSchema>;

const heldSchema = z.object({
  state: z.literal('held'),
  /** When Kite said it: now, or the close of `day` when the member's session has ended. */
  at: instant,
  live: z.boolean(),
  /** Why Kite was not read just now, when the page shows the last close. */
  missed: z.enum(['not-linked', 'logged-out', 'failed']).optional(),
  day: day.nullable(),
  value: z.number(),
  invested: z.number(),
  today: z.number().nullable(),
  /** Of what they were worth before it, for the holdings whose `today` is said. */
  todayFraction: z.number().nullable(),
  holdings: z.array(holdingSchema),
  /** Everything, after looking inside each fund; `unread` is a fund with nothing to look inside. */
  mix: z.array(z.object({ asset: z.enum([...ASSETS, 'unread']), value: z.number() })),
  /** The mix's debt by its credit; `unknown` is debt no disclosed line rates. */
  credit: z.array(z.object({ credit: z.enum(CREDITS), value: z.number() })),
  stocks: z.array(exposureSchema),
  /** Equity in futures on an index, and in stocks no ISIN names: in the mix, not in `stocks`. */
  unnamedEquity: z.number(),
  /** Of `unnamedEquity`, what is in overseas funds' units, whose stocks no Indian house names. */
  overseasUnits: z.number(),
  sectors: z.array(sectorShareSchema),
  sizes: z.array(sizeShareSchema),
  overlaps: z.array(overlapSchema),
  costs: z.object({
    yearly: z.number(),
    /** Of the money in funds and ETFs. */
    ratio: z.number().nullable(),
    directSaves: z.number(),
    atMedian: z.number().nullable(),
  }),
  findings: z.array(findingSchema),
  changes: z.object({ month: day, from: day, items: z.array(changeSchema) }).nullable(),
  history: z.object({
    /** Its latest nights on file, oldest first, each on its date. */
    nights: z.array(
      z.object({
        day,
        worth: z.number(),
        /** Each line's quantity at its average buy price. */
        invested: z.number(),
        /** Where Kite was not read that night: the night whose holdings were valued at its closes. */
        heldOn: day.nullable(),
      }),
    ),
    /**
     * Money-weighted, from the first night on file to the last; a year's rate once they are a
     * year apart, else the period's. Null before they are 90 days apart.
     */
    returns: z.object({ from: day, to: day, annual: z.boolean(), rate: z.number() }).nullable(),
  }),
  /** The fund the sector and size shares are read against, with the month it was disclosed. */
  benchmark: z.object({ name: z.string(), month: day }).nullable(),
  /** Null before any night is read. */
  yours: yoursSchema.nullable(),
});

export type HeldPortfolio = z.infer<typeof heldSchema>;

export const portfolioSchema = z.discriminatedUnion('state', [
  heldSchema,
  /** No Zerodha linked, or linked but never read: the page offers the link or the login. */
  z.object({ state: z.literal('unread'), reason: z.enum(['not-linked', 'logged-out', 'failed']) }),
  z.object({ state: z.literal('empty'), at: instant }),
]);

export type Portfolio = z.infer<typeof portfolioSchema>;
